import {
  ArrowRight,
  BarChart3,
  ChevronRight,
  ClipboardList,
  Megaphone,
  Target,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ProgressRing } from "@/components/ui/progress-ring";
import { useActionPlans } from "@/features/action-plans/api/hooks";
import { useProfileQuery } from "@/features/communications/api";
import { InvitationNotices } from "@/features/partners/components/InvitationNotices";
import { useProgress } from "@/features/questionnaire/api";
import { thematicTotals } from "@/features/questionnaire/progress-utils";
import type { QuestionnaireProgress } from "@/features/questionnaire/types";
import { useAuth } from "@/lib/auth/auth-context";
import {
  canAccessReports,
  isAdmin,
  isSuperUser,
  landingPath,
  showPartnerTab,
} from "@/lib/auth/roles";
import { ACCOUNTS_ORIGIN } from "@/lib/config";
import { THEMATIC, type ThematicKey, thematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

/**
 * Enterprise home / action hub. Leads with the progress hero — the overall
 * self-diagnostic ring broken out across the four sustainability pillars — then
 * the enterprise activities as a set of cards (each with a live status chip and
 * a list of quick links), and finally any role-gated management tools, demoted
 * to a quiet row so they don't compete with the core activities.
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hasProfile = !!user?.enterprise_profile_id;

  // Status data — only fetched for users with an enterprise profile, so we never
  // fire (or 403) these for admin-only accounts. Chips render fail-soft: absent
  // until their query resolves with data.
  const progress = useProgress({ enabled: hasProfile });
  const plans = useActionPlans({ enabled: hasProfile });
  const comm = useProfileQuery({ enabled: hasProfile });

  // `contact_email`, never `email`: for a training participant the latter is a
  // fabricated identifier on a non-routable domain, and this greeting is the
  // first thing they see after following their link — before they have filled
  // a profile, so before there is any name to fall back on.
  const name =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.contact_email ||
    user?.email ||
    "";
  const roleLabel = isAdmin(user)
    ? "Admin"
    : isSuperUser(user)
      ? "Super User"
      : t("dashboard.noRole");

  const answered = progress.data?.answered_actions ?? 0;
  const started = answered > 0;
  // The "voir mes résultats" shortcut is only offered once the questionnaire
  // has been completed — the old app disables it until then (C-5).
  const completed = Boolean(progress.data?.is_completed);

  const tools: Tool[] = [
    canAccessReports(user) && {
      to: "/admin",
      icon: BarChart3,
      title: t("dashboard.more.reports.title"),
      desc: t("dashboard.more.reports.desc"),
    },
    showPartnerTab(user) && {
      to: "/partner-group",
      icon: Users,
      title: t("dashboard.more.partners.title"),
      desc: t("dashboard.more.partners.desc"),
    },
  ].filter(Boolean) as Tool[];

  const published = Boolean(comm.data?.is_published && comm.data?.slug);

  // Role-appropriate landing (after all hooks, to respect rules-of-hooks):
  // experts get their own dashboard; a partner / super-user without a member
  // licence gets the group area. The enterprise dashboard is only home for
  // members / licensed partners / admins.
  const home = landingPath(user);
  if (user && home !== "/dashboard") {
    return <Navigate to={home} replace />;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-serif text-3xl font-semibold tracking-tight [text-wrap:balance]">
          {t("dashboard.welcome", { name })}
        </h1>
        <span className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-sm font-medium text-secondary-foreground">
          {t("dashboard.roleLabel")}: {roleLabel}
        </span>
      </div>

      <InvitationNotices />

      {hasProfile &&
        (progress.data ? (
          <ResumeHero data={progress.data} />
        ) : (
          <ResumeHeroSkeleton />
        ))}

      <section>
        <SectionEyebrow>{t("dashboard.sections.journey")}</SectionEyebrow>
        <div className="grid gap-4 md:grid-cols-3">
          <FeatureCard
            tone="governance"
            icon={ClipboardList}
            title={t("dashboard.hub.questionnaire.title")}
            desc={t("dashboard.hub.questionnaire.desc")}
            status={assessStatus(progress.data)}
            links={[
              {
                to: "/questionnaire",
                label: started
                  ? t("dashboard.hub.questionnaire.ctaResume")
                  : t("dashboard.hub.questionnaire.cta"),
                lead: true,
              },
              ...(completed
                ? [
                    {
                      to: "/questionnaire",
                      search: { view: "results" },
                      label: t("dashboard.hub.questionnaire.results"),
                    },
                  ]
                : []),
              {
                to: "/questionnaire",
                search: { view: "issues" },
                label: t("dashboard.hub.questionnaire.addIssues"),
              },
            ]}
          />
          <FeatureCard
            tone="economic"
            icon={Target}
            title={t("dashboard.hub.actionPlans.title")}
            desc={t("dashboard.hub.actionPlans.desc")}
            status={planStatus(plans.data)}
            links={[
              {
                to: "/action-plans",
                label: t("dashboard.hub.actionPlans.cta"),
                lead: true,
              },
              {
                to: "/action-plans",
                label: t("dashboard.hub.actionPlans.consult"),
              },
            ]}
          />
          <FeatureCard
            tone="social"
            icon={Megaphone}
            title={t("dashboard.hub.communications.title")}
            desc={t("dashboard.hub.communications.desc")}
            status={commStatus(comm.data)}
            links={[
              {
                to: "/communications",
                label: t("dashboard.hub.communications.cta"),
                lead: true,
              },
              ...(published
                ? [
                    {
                      href: `${ACCOUNTS_ORIGIN}/communication/${comm.data!.slug}/`,
                      label: t("dashboard.hub.communications.view"),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </section>

      {tools.length > 0 && (
        <section>
          <SectionEyebrow>{t("dashboard.sections.manage")}</SectionEyebrow>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {tools.map((tool) => (
              <ToolRow key={tool.to} {...tool} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 flex items-baseline gap-3">
      <h2 className="text-xs font-bold uppercase tracking-[0.09em] text-muted-foreground">
        {children}
      </h2>
      <span aria-hidden className="h-px flex-1 bg-border" />
    </div>
  );
}

/* ---- status chips (fail-soft: null → no chip) ---------------------------- */

type StatusTone = "done" | "doing" | "todo";
interface Status {
  labelKey: string;
  tone: StatusTone;
}

function assessStatus(data?: QuestionnaireProgress): Status | null {
  if (!data) return null;
  const { answered_actions: a, total_actions: total } = data;
  if (total > 0 && a >= total) return { labelKey: "complete", tone: "done" };
  if (a > 0) return { labelKey: "inProgress", tone: "doing" };
  return { labelKey: "notStarted", tone: "todo" };
}

function planStatus(data?: { length: number }): Status | null {
  if (!data) return null;
  return data.length > 0
    ? { labelKey: "planActive", tone: "done" }
    : { labelKey: "planNone", tone: "todo" };
}

function commStatus(data?: { is_published?: boolean }): Status | null {
  if (!data) return null;
  return data.is_published
    ? { labelKey: "published", tone: "done" }
    : { labelKey: "unpublished", tone: "todo" };
}

const STATUS_DOT: Record<StatusTone, string> = {
  done: "bg-success",
  doing: "bg-warning",
  todo: "bg-muted-foreground/40",
};

function StatusChip({ labelKey, tone }: Status) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
      <span aria-hidden className={cn("size-1.5 rounded-full", STATUS_DOT[tone])} />
      {t(`dashboard.status.${labelKey}`)}
    </span>
  );
}

/* ---- feature card + link list -------------------------------------------- */

interface CardLink {
  label: string;
  to?: string;
  search?: Record<string, unknown>;
  href?: string;
  lead?: boolean;
}

interface FeatureCardProps {
  tone: ThematicKey;
  icon: LucideIcon;
  title: string;
  desc: string;
  status: Status | null;
  links: CardLink[];
}

function FeatureCard({ tone, icon: Icon, title, desc, status, links }: FeatureCardProps) {
  const th = THEMATIC[tone];
  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span
          aria-hidden
          className={cn("grid size-11 place-items-center rounded-xl", th.softBg, th.ink)}
        >
          <Icon className="size-5" />
        </span>
        {status && <StatusChip {...status} />}
      </div>
      <h3 className="text-lg font-bold tracking-tight">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
      <div className="mt-4 flex flex-col">
        {links.map((link) => (
          <LinkRow key={link.label} {...link} />
        ))}
      </div>
    </div>
  );
}

function LinkRow({ label, to, search, href, lead }: CardLink) {
  const cls = cn(
    "group flex items-center gap-2.5 border-t border-border py-2.5 text-sm font-semibold transition-[padding,color] hover:pl-1.5",
    lead ? "text-primary" : "text-foreground/80 hover:text-primary",
  );
  const chevron = (
    <ChevronRight
      aria-hidden
      className="ml-auto size-4 opacity-50 transition-transform group-hover:translate-x-0.5 group-hover:opacity-100"
    />
  );
  if (href) {
    return (
      <a className={cls} href={href}>
        {label}
        {chevron}
      </a>
    );
  }
  return (
    <Link className={cls} to={to as string} search={search as never}>
      {label}
      {chevron}
    </Link>
  );
}

/* ---- management tool row -------------------------------------------------- */

interface Tool {
  to: string;
  icon: LucideIcon;
  title: string;
  desc: string;
}

function ToolRow({ to, icon: Icon, title, desc }: Tool) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3.5 rounded-xl border border-border bg-card px-4 py-3.5 transition-colors hover:bg-secondary"
    >
      <span
        aria-hidden
        className="grid size-9 flex-none place-items-center rounded-lg bg-secondary text-muted-foreground group-hover:bg-card"
      >
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold">{title}</span>
        <span className="block truncate text-xs text-muted-foreground">{desc}</span>
      </span>
      <ChevronRight aria-hidden className="size-4 flex-none text-muted-foreground" />
    </Link>
  );
}

/* ---- progress hero -------------------------------------------------------- */

/** A single pillar's progress bar for the hero breakdown. */
function Pillar({
  name,
  cssClass,
  answered,
  total,
}: {
  name: string;
  cssClass: string;
  answered: number;
  total: number;
}) {
  const th = thematicClasses(cssClass);
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2.5">
      <span aria-hidden className={cn("size-2.5 flex-none rounded-sm", th.solid)} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-xs font-semibold text-foreground/80">{name}</span>
          <span className="text-xs tabular-nums text-muted-foreground">
            {answered}/{total}
          </span>
        </span>
        <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-secondary">
          <span
            className={cn("block h-full rounded-full transition-[width] duration-700", th.solid)}
            style={{ width: `${pct}%` }}
          />
        </span>
      </span>
    </div>
  );
}

/**
 * Progress-aware self-assessment hero: overall completion ring, a per-pillar
 * breakdown, and the resume CTA. Rendered only for users with an enterprise
 * profile (so it reports real completion).
 */
/** Placeholder shown while the progress query loads, so the hero doesn't pop
 *  in and the layout stays stable. */
function ResumeHeroSkeleton() {
  return (
    <Card className="relative overflow-hidden" aria-hidden>
      <span className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-theme-governance via-theme-economic to-theme-environment" />
      <CardContent className="grid animate-pulse gap-6 p-8 pl-8 lg:grid-cols-[auto_1fr_auto] lg:items-center">
        <div className="size-[116px] rounded-full bg-muted" />
        <div className="min-w-0 space-y-3">
          <div className="h-5 w-48 rounded bg-muted" />
          <div className="h-4 w-64 rounded bg-muted" />
          <div className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-3 w-full rounded bg-muted" />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ResumeHero({ data }: { data: QuestionnaireProgress }) {
  const { t } = useTranslation();
  const { answered_actions: answered, total_actions: total } = data;
  const remaining = Math.max(0, total - answered);
  const pct = total > 0 ? (answered / total) * 100 : 0;
  const started = answered > 0;
  const pillars = thematicTotals(data);

  return (
    <Card className="relative overflow-hidden">
      {/* Left spectrum accent — echoes the shell identity strip. */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-theme-governance via-theme-economic to-theme-environment"
      />
      <CardContent className="grid gap-6 p-8 pl-8 lg:grid-cols-[auto_1fr_auto] lg:items-center">
        <ProgressRing value={pct} size={116}>
          <div>
            <div className="text-2xl font-extrabold tabular-nums">{Math.round(pct)}%</div>
            <div className="text-xs font-medium text-muted-foreground">
              {t("dashboard.resume.assessed")}
            </div>
          </div>
        </ProgressRing>

        <div className="min-w-0">
          <h2 className="text-xl font-bold tracking-tight">
            {started ? t("dashboard.resume.title") : t("dashboard.resume.titleStart")}
          </h2>
          <p className="mt-1 max-w-prose text-sm text-muted-foreground">
            {started
              ? t("dashboard.resume.subtitle", { answered, total })
              : t("dashboard.resume.subtitleStart")}
          </p>
          {pillars.length > 0 && (
            <div className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {pillars.map((p) => (
                <Pillar
                  key={p.id}
                  name={p.name}
                  cssClass={p.cssClass}
                  answered={p.answered}
                  total={p.total}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2.5 lg:items-end">
          <Button asChild size="lg">
            <Link to="/questionnaire" search={{ mode: "focus" }}>
              {started
                ? t("dashboard.resume.continue", { count: remaining })
                : t("dashboard.resume.start")}
              <ArrowRight aria-hidden />
            </Link>
          </Button>
          {started && (
            <Button asChild variant="ghost" size="sm">
              <Link to="/questionnaire" search={{ mode: "browse" }}>
                {t("dashboard.resume.browse")}
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
